/**
 * How a rung of the live appeal reads, in one place for the console and the big screen (GH-882).
 *
 * Owner, 2026-10-04: "add option for named targets, not just 1 of x @ $y but also like 'feed x
 * orphans @ $y each'". A rung may carry a named target — a verb and a unit noun the organizer types
 * — and then the ask reads as a sentence about what the room provides rather than a count of
 * amounts. One commitment is one unit, so the count is the ladder's own `committedCount`: these
 * functions only arrange words around figures the server already summed.
 *
 * Every word of the unit is the organizer's (trimmed and length-capped on the server); nothing
 * here supplies a noun, so the product never assumes what a room is raising money for.
 */
import { formatMoney } from "./v1.js";
const count = (value) => value.toLocaleString("en-US");
/**
 * What the rung asks for, as the room hears it.
 *
 *  - named, with a target: "Feed 10 orphans @ $50.00 each"
 *  - named, no target:     "Feed orphans @ $50.00 each"
 *  - plain, with a target: "10 @ $1,000.00"
 *  - plain, no target:     "$1,000.00"
 */
export function appealAskWords(rung) {
    const money = formatMoney(rung.amount);
    if (rung.unit) {
        const parts = [
            rung.unit.verb,
            rung.targetCount === null ? null : count(rung.targetCount),
            rung.unit.noun,
        ].filter((part) => part !== null);
        return `${parts.join(" ")} @ ${money} each`;
    }
    return rung.targetCount === null ? money : `${count(rung.targetCount)} @ ${money}`;
}
/**
 * The rung's progress: "3 of 10 orphans", "12 orphans", "3 of 10", or "3 committed" — the count
 * of commitments, which for a named target is the count of units.
 */
export function appealCountWords(committed, rung) {
    const progress = rung.targetCount === null
        ? count(committed)
        : `${count(committed)} of ${count(rung.targetCount)}`;
    if (rung.unit)
        return `${progress} ${rung.unit.noun}`;
    return rung.targetCount === null ? `${progress} committed` : progress;
}
//# sourceMappingURL=appeal-copy.js.map